import { useEffect, useRef, useState } from 'react';
import { runPython } from './PyodideRunner';
import api from '../api/client';

export default function TestRunner({ code, taskId, testCases = [] }) {
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSubmissionId, setActiveSubmissionId] = useState(null);
  const quickCheckAbortRef = useRef(null);
  const submitPollTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (quickCheckAbortRef.current) quickCheckAbortRef.current.abort();
      if (submitPollTimerRef.current) clearInterval(submitPollTimerRef.current);
    };
  }, []);

  async function handleQuickCheck() {
    if (!testCases.length) return;
    if (quickCheckAbortRef.current) quickCheckAbortRef.current.abort();
    const abortController = new AbortController();
    quickCheckAbortRef.current = abortController;

    setLoading(true);
    setStatus('checking');
    setSubmitResult(null);
    const newResults = [];

    for (const tc of testCases) {
      if (abortController.signal.aborted) break;
      try {
        const result = await runPython(code, tc.input || '', {}, { signal: abortController.signal });
        const actual = result.success ? result.output : `Ошибка: ${result.error}`;
        const expected = (tc.expected_output || tc.output || '').trim();
        const passed = result.success && actual.trim() === expected;
        newResults.push({
          input: tc.input || '',
          expected,
          actual: actual.trim(),
          passed,
          error: result.success ? null : result.error,
        });
      } catch (err) {
        if (abortController.signal.aborted) {
          break;
        }
        newResults.push({
          input: tc.input || '',
          expected: tc.expected_output || tc.output || '',
          actual: '',
          passed: false,
          error: err.message,
        });
      }
    }

    setResults(newResults);
    if (abortController.signal.aborted) {
      setStatus(null);
    } else {
      const allPassed = newResults.every((r) => r.passed);
      setStatus(allPassed ? 'passed' : 'failed');
    }
    setLoading(false);
  }

  function stopQuickCheck() {
    if (quickCheckAbortRef.current) quickCheckAbortRef.current.abort();
  }

  async function pollSubmission(submissionId) {
    try {
      const res = await api.get(`/judge/submission/${submissionId}/`);
      const data = res.data;
      if (data.status === 'completed') {
        setSubmitResult({
          status: data.result?.failed === 0 && data.result?.passed > 0 ? 'accepted' : 'error',
          result: data.result,
          execution_time_ms: data.execution_time_ms,
        });
        setStatus(data.result?.failed === 0 && data.result?.passed > 0 ? 'passed' : 'failed');
        setLoading(false);
        setActiveSubmissionId(null);
        if (submitPollTimerRef.current) clearInterval(submitPollTimerRef.current);
      } else if (data.status === 'cancelled') {
        setSubmitResult({ status: 'cancelled', message: 'Остановлено' });
        setStatus('error');
        setLoading(false);
        setActiveSubmissionId(null);
        if (submitPollTimerRef.current) clearInterval(submitPollTimerRef.current);
      } else if (data.status === 'error') {
        setSubmitResult({ status: 'error', message: 'Ошибка выполнения' });
        setStatus('error');
        setLoading(false);
        setActiveSubmissionId(null);
        if (submitPollTimerRef.current) clearInterval(submitPollTimerRef.current);
      }
    } catch {
      // ignore polling errors; keep trying
    }
  }

  async function handleSubmit() {
    if (submitPollTimerRef.current) clearInterval(submitPollTimerRef.current);
    setLoading(true);
    setStatus('checking');
    setResults([]);
    setSubmitResult(null);
    try {
      const res = await api.post('/judge/submit/', {
        task_id: taskId,
        code,
      });
      const submissionId = res.data.submission_id;
      setActiveSubmissionId(submissionId);
      await pollSubmission(submissionId);
      submitPollTimerRef.current = setInterval(() => pollSubmission(submissionId), 800);
    } catch (err) {
      setStatus('error');
      setSubmitResult({
        status: 'error',
        message: err.response?.data?.detail || 'Ошибка отправки решения',
      });
    } finally {
      // loading завершаем в pollSubmission
    }
  }

  async function handleStopSubmit() {
    if (!activeSubmissionId) return;
    try {
      await api.post(`/judge/submission/${activeSubmissionId}/stop/`);
    } catch {
      // ignore
    }
  }

  const statusLabels = {
    checking: { text: 'Проверка...', className: 'status-checking' },
    passed: { text: '✅ Все тесты пройдены', className: 'status-passed' },
    failed: { text: '❌ Есть ошибки', className: 'status-failed' },
    error: { text: '⚠️ Ошибка', className: 'status-error' },
  };

  return (
    <div className="test-runner">
      <div className="test-runner-actions">
        <button
          onClick={handleQuickCheck}
          disabled={loading || !testCases.length}
          className="btn btn-secondary"
        >
          {loading && status === 'checking' && !submitResult ? (
            <><span className="spinner-small"></span> Проверка...</>
          ) : (
            '⚡ Быстрая проверка'
          )}
        </button>
        <button
          onClick={stopQuickCheck}
          disabled={!loading || status !== 'checking' || !!activeSubmissionId}
          className="btn btn-secondary"
        >
          Остановить
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="btn btn-primary"
        >
          {loading && submitResult !== null ? (
            <><span className="spinner-small"></span> Отправка...</>
          ) : (
            '📤 Отправить решение'
          )}
        </button>
        <button
          onClick={handleStopSubmit}
          disabled={!activeSubmissionId}
          className="btn btn-secondary"
        >
          Остановить
        </button>
      </div>

      {status && (
        <div className={`test-status ${statusLabels[status]?.className || ''}`}>
          {statusLabels[status]?.text}
        </div>
      )}

      {results.length > 0 && (
        <div className="test-results">
          <h4>Результаты проверки</h4>
          <table className="results-table">
            <thead>
              <tr>
                <th>Вход</th>
                <th>Ожидаемый</th>
                <th>Полученный</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i} className={r.passed ? 'row-passed' : 'row-failed'}>
                  <td><code>{r.input}</code></td>
                  <td><code>{r.expected}</code></td>
                  <td><code>{r.actual}</code></td>
                  <td>{r.passed ? '✅' : '❌'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {submitResult && (
        <div className="submit-result">
          <h4>Результат отправки</h4>
          <p className={submitResult.status === 'accepted' ? 'text-success' : 'text-error'}>
            {submitResult.status === 'accepted' ? '✅ Решение принято!' : submitResult.message || 'Решение не принято'}
          </p>
          {submitResult.score !== undefined && (
            <p>Баллы: {submitResult.score}</p>
          )}
        </div>
      )}
    </div>
  );
}
